import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, scrollToId } from "./shared";
import { IMG, MASTERPLAN_ZONES } from "../data/site";
import { ArrowUpRight } from "lucide-react";
import { Gallery } from "./Gallery";

const FEATURES = [
  "Residential Apartments", "Independent Floors", "Villas", "Residential Plots",
  "Commercial Shops", "Commercial Plots", "Studio Suites", "Retail",
  "Green Spaces", "Lifestyle Infrastructure", "Sports", "Community Infrastructure",
];

export function Township() {
  const [zone, setZone] = useState(MASTERPLAN_ZONES[0]);

  return (
    <section id="gyc" className="relative py-28 lg:py-40 bg-[#0D0D0D]" data-testid="gyc-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>The Township</SectionTag>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-end mb-14">
          <SectionTitle>
            GAUR YAMUNA CITY
            <span className="block text-2xl sm:text-3xl lg:text-4xl mt-4 text-[#A1A1AA] italic">
              &ldquo;A Township Within a Transforming Corridor.&rdquo;
            </span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-lg">
              An integrated township on the Yamuna Expressway - residential towers, plotted sectors,
              villa enclaves, retail and community infrastructure - positioned at the centre of the
              airport-led development story.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gyc-feature-pills">
            {FEATURES.map((f) => (
              <span key={f} className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37]/20 text-[#A1A1AA] rounded-full hover:border-[#D4AF37]/60 hover:text-[#F3E5AB] transition-colors cursor-default">
                {f}
              </span>
            ))}
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          <Reveal className="lg:col-span-3" delay={0.1}>
            <div className="relative h-[380px] lg:h-[520px] overflow-hidden rounded-[2px] border border-[#D4AF37]/15 group" data-testid="masterplan-visual">
              <motion.img
                src={IMG.masterplan}
                alt="Gaur Yamuna City entrance gate, Sector 19 Yamuna Expressway"
                className="w-full h-full object-cover img-cinematic"
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 lg:p-8 flex items-end justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Interactive Masterplan</p>
                  <p className="font-serif-lux text-2xl text-[#F5F5F0]">{zone.label} Zone</p>
                </div>
                <button
                  data-testid="masterplan-explore-btn"
                  onClick={() => scrollToId("inventory")}
                  className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#F3E5AB] hover:text-white transition-colors"
                >
                  View Inventory <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-2 flex flex-col gap-3" data-testid="masterplan-zones">
            {MASTERPLAN_ZONES.map((z, i) => {
              const active = zone.id === z.id;
              return (
                <Reveal key={z.id} delay={0.08 + i * 0.05}>
                  <button
                    data-testid={`masterplan-zone-${z.id}`}
                    onClick={() => setZone(z)}
                    className={`w-full text-left p-5 border rounded-[2px] transition-all duration-500 ${
                      active
                        ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]"
                        : "border-[#D4AF37]/12 bg-[#111111] hover:border-[#D4AF37]/35"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${active ? "text-[#D4AF37]" : "text-[#A1A1AA]"}`}>
                        {z.label}
                      </span>
                      <span className={`h-px flex-1 mx-4 transition-colors ${active ? "bg-[#D4AF37]/50" : "bg-[#D4AF37]/10"}`} />
                      <ArrowUpRight size={14} className={active ? "text-[#D4AF37]" : "text-[#3f3f46]"} />
                    </div>
                    <AnimatePresence mode="wait">
                      {active && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="text-sm text-[#A1A1AA] leading-relaxed pt-2">{z.desc}</p>
                          <div className="flex flex-wrap gap-2 pt-3">
                            {z.items.map((it) => (
                              <span key={it} className="text-[10px] tracking-[0.15em] uppercase text-[#F3E5AB]/70 border border-[#D4AF37]/25 px-2.5 py-1 rounded-full">{it}</span>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-6 relative h-[280px] lg:h-[420px] overflow-hidden rounded-[2px] border border-[#D4AF37]/20" data-testid="gyc-official-masterplan">
            <img
              src={IMG.gycMasterPlan}
              alt="Official Gaur Yamuna City master plan showing all Parkview sectors on Yamuna Expressway"
              className="w-full h-full object-contain bg-[#0A0A0A]"
            />
            <div className="absolute top-0 inset-x-0 p-5 bg-gradient-to-b from-black/80 to-transparent">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Official Township Map</p>
              <p className="text-[12px] text-[#A1A1AA]">101 hectares (250 acres) integrated township &mdash; source: Gaursons India, indicative and subject to revision.</p>
            </div>
          </div>
        </Reveal>
        <Gallery />
      </div>
    </section>
  );
}
